import React, { useState, useCallback } from 'react';
import { Trash2, Plus, Check, AlertCircle } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import Select from '../common/Select';
import { email, phone } from '../../utils/validators';

/**
 * Componente para agregar/editar tutores
 * Permite registrar múltiples tutores con validaciones
 */
const TutoresForm = ({ tutores = [], onTutoresChange, errors = {} }) => {
  const [tutoresLocales, setTutoresLocales] = useState(tutores);
  const [editando, setEditando] = useState(null);
  const [nuevoTutor, setNuevoTutor] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    relacion: '',
    ocupacion: '',
    es_contacto_principal: tutores.length === 0 // El primero es contacto principal
  });

  const relacionesDisponibles = [
    { value: 'Padre', label: 'Padre' },
    { value: 'Madre', label: 'Madre' },
    { value: 'Tutor', label: 'Tutor Legal' },
    { value: 'Abuelo', label: 'Abuelo/a' },
    { value: 'Otro', label: 'Otro' }
  ];

  const validarTutor = useCallback((tutor) => {
    const erroresLocales = {};

    if (!tutor.nombre?.trim()) {
      erroresLocales.nombre = 'El nombre es obligatorio';
    }

    if (!tutor.apellido?.trim()) {
      erroresLocales.apellido = 'El apellido es obligatorio';
    }

    if (!tutor.email?.trim()) {
      erroresLocales.email = 'El email es obligatorio';
    } else if (email(tutor.email)) {
      erroresLocales.email = email(tutor.email);
    }

    if (!tutor.telefono?.trim()) {
      erroresLocales.telefono = 'El teléfono es obligatorio';
    } else if (phone(tutor.telefono)) {
      erroresLocales.telefono = phone(tutor.telefono);
    }

    if (!tutor.relacion) {
      erroresLocales.relacion = 'La relación es obligatoria';
    }

    return erroresLocales;
  }, []);

  const agregarTutor = useCallback(() => {
    const erroresValidacion = validarTutor(nuevoTutor);

    if (Object.keys(erroresValidacion).length > 0) {
      // Mostrar errores en el formulario
      console.error('Errores de validación:', erroresValidacion);
      return;
    }

    const tutorAgregado = { ...nuevoTutor, id: Date.now() };
    const nuevosTutores = [...tutoresLocales, tutorAgregado];

    // Si es el primer tutor, hacerlo contacto principal automáticamente
    if (nuevosTutores.length === 1) {
      nuevosTutores[0].es_contacto_principal = true;
    }

    setTutoresLocales(nuevosTutores);
    onTutoresChange(nuevosTutores);

    // Limpiar formulario
    setNuevoTutor({
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      relacion: '',
      ocupacion: '',
      es_contacto_principal: false
    });
  }, [nuevoTutor, tutoresLocales, onTutoresChange, validarTutor]);

  const eliminarTutor = useCallback((id) => {
    const nuevosTutores = tutoresLocales.filter(t => t.id !== id);

    // Si el tutor eliminado era contacto principal, asignar el primero
    if (nuevosTutores.length > 0 && !nuevosTutores.some(t => t.es_contacto_principal)) {
      nuevosTutores[0].es_contacto_principal = true;
    }

    setTutoresLocales(nuevosTutores);
    onTutoresChange(nuevosTutores);
  }, [tutoresLocales, onTutoresChange]);

  const establecerContactoPrincipal = useCallback((id) => {
    const nuevosTutores = tutoresLocales.map(t => ({
      ...t,
      es_contacto_principal: t.id === id
    }));

    setTutoresLocales(nuevosTutores);
    onTutoresChange(nuevosTutores);
  }, [tutoresLocales, onTutoresChange]);

  const cambiarTutor = useCallback((campo, valor) => {
    setNuevoTutor(prev => ({
      ...prev,
      [campo]: valor
    }));
  }, []);

  return (
    <div className="space-y-6">
      {/* Título */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Datos de Tutores/Padres</h3>
        <p className="text-sm text-gray-500 mt-1">Registre al menos un tutor o padre de familia</p>
      </div>

      {/* Listado de tutores registrados */}
      {tutoresLocales.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Tutores registrados ({tutoresLocales.length})</h4>
          {tutoresLocales.map((tutor) => (
            <div
              key={tutor.id}
              className={`p-4 border-2 rounded-lg transition-colors ${
                tutor.es_contacto_principal
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">
                      {tutor.nombre} {tutor.apellido}
                    </p>
                    <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                      {tutor.relacion}
                    </span>
                    {tutor.es_contacto_principal && (
                      <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded flex items-center gap-1">
                        <Check size={12} /> Contacto Principal
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Email: {tutor.email}
                  </p>
                  <p className="text-sm text-gray-600">
                    Teléfono: {tutor.telefono}
                  </p>
                  {tutor.ocupacion && (
                    <p className="text-sm text-gray-600">
                      Ocupación: {tutor.ocupacion}
                    </p>
                  )}
                </div>

                <div className="flex gap-2 ml-4">
                  {!tutor.es_contacto_principal && (
                    <button
                      type="button"
                      onClick={() => establecerContactoPrincipal(tutor.id)}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      title="Establecer como contacto principal"
                    >
                      Establecer Principal
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => eliminarTutor(tutor.id)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Eliminar tutor"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Alerta si no hay tutores */}
      {tutoresLocales.length === 0 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
          <AlertCircle size={20} className="text-yellow-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">
              No hay tutores registrados
            </p>
            <p className="text-sm text-yellow-700">
              Debe registrar al menos un tutor antes de enviar el formulario
            </p>
          </div>
        </div>
      )}

      {/* Formulario para agregar nuevo tutor */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Plus size={18} /> Agregar Nuevo Tutor
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre *
            </label>
            <Input
              type="text"
              value={nuevoTutor.nombre}
              onChange={(e) => cambiarTutor('nombre', e.target.value)}
              placeholder="Juan"
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Apellido *
            </label>
            <Input
              type="text"
              value={nuevoTutor.apellido}
              onChange={(e) => cambiarTutor('apellido', e.target.value)}
              placeholder="Pérez"
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <Input
              type="email"
              value={nuevoTutor.email}
              onChange={(e) => cambiarTutor('email', e.target.value)}
              placeholder="juan@example.com"
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Teléfono *
            </label>
            <Input
              type="tel"
              value={nuevoTutor.telefono}
              onChange={(e) => cambiarTutor('telefono', e.target.value)}
              placeholder="0987654321"
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Relación *
            </label>
            <Select
              value={nuevoTutor.relacion}
              onChange={(e) => cambiarTutor('relacion', e.target.value)}
              options={relacionesDisponibles}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ocupación (Opcional)
            </label>
            <Input
              type="text"
              value={nuevoTutor.ocupacion}
              onChange={(e) => cambiarTutor('ocupacion', e.target.value)}
              placeholder="Ingeniero/a"
              className="w-full"
            />
          </div>
        </div>

        <Button
          onClick={agregarTutor}
          className="mt-4 w-full md:w-auto"
          variant="primary"
        >
          <Plus size={18} /> Agregar Tutor
        </Button>
      </div>

      {/* Errores generales */}
      {errors.tutores && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{errors.tutores}</p>
        </div>
      )}
    </div>
  );
};

export default TutoresForm;
